import type { GameId } from "@bing/shared";
import backgammon from "./games/backgammon.jpg";
import bluff from "./games/bluff.jpg";
import chess from "./games/chess.jpg";
import dooz from "./games/dooz.jpg";
import hearts from "./games/hearts.jpg";
import hokm from "./games/hokm.jpg";
import ladders from "./games/ladders.jpg";
import mensch from "./games/mensch.jpg";
import pasur from "./games/pasur.jpg";
import shelem from "./games/shelem.jpg";
import snake from "./games/snake.jpg";
import sudoku from "./games/sudoku.jpg";

/**
 * Per-game promotional artwork, shared by the lobby tiles and the invite banners.
 * The JPEGs are ALSO mirrored to `apps/web/public/banners/<game>.jpg` so the bot can attach
 * them to the Telegram invite PV (a stable public URL, not a hashed bundle asset).
 *
 * The four BOARD games (شطرنج / منچ / تخته نرد / دوز) are cut from ONE 1376×768 render each — a
 * walnut-and-brass chess set, a lacquered منچ board with its four coloured tokens and a die,
 * and a khatam-inlaid تخته نرد board with gold/obsidian checkers — all on the same emerald
 * felt under one warm spotlight, textless (every surface draws its own label). Each render
 * feeds THREE derivatives so a board game has one visual identity like the card games:
 *   • `public/banners/banner-<game>.jpg` — the full 1376×768 frame (tutorial link-preview +
 *     invite PV). They used to be 512×286 placeholders, which read soft in Telegram's large
 *     preview next to the card banners.
 *   • `assets/games/<game>.jpg` — 420×313, framed so the middle ~72% (all the lobby's
 *     `object-cover` box ever shows) holds the subject and the bottom-right stays quiet for
 *     the game name.
 *   • `assets/guides/<game>.jpg` — a 360×360 square for the راهنما row's 48×48 thumbnail.
 * The SOURCES live in `docs/stitch/board-guide-art/` — keep them: every size above is cut
 * from them, and a derivative cannot be re-cut from another derivative. ⚠️ Those four PNGs are
 * all 1376×768 — the banner's OWN size, which is how شطرنج/منچ/تخته نرد were delivered — so they
 * can re-cut every size above losslessly but can never feed a surface larger than the banner.
 * دوز، حکم and بی‌دل are the exceptions: their full-resolution renders are archived in the
 * SAME folder as `<game>-master.jpeg`. `docs/stitch/board-guide-art/README.md` has the table.
 *
 * دوز's three files are the REAL render, cut 2026-09-03 (they were token-drawn placeholders
 * before that). Its master arrived at 2752×1536 — the only GAME master that is exactly 2× the
 * banner, so its banner is a clean downscale where حکم's and بی‌دل's are crops — and is archived
 * at `docs/stitch/board-guide-art/dooz-master.jpeg`, which `apps/web/tools/dooz-art/cut.cjs`
 * reads by default. It cuts all three from it, DERIVING each crop from features it measures in
 * the master (the walnut frame's bounding box, the disc cluster's) rather than from hand-typed
 * pixels, and re-reading every output's own header to assert the size. Re-run it if the framing
 * ever needs to change; the prompts that produced the render are in `PROMPTS.md`.
 *
 * حکم and بی‌دل got the same treatment on 2026-09-03, from 4:3 masters — `card-art/cut.cjs`.
 * Their tile AND banner used to be two unrelated pictures (a photographic tile, a stylized
 * Persian-bordered banner); they are one render now, and so are شلم since 2026-10-22 and پاسور since
 * 2026-10-30 (the operator's 2400×1792 masters, cut by the same script — their subject boxes measured
 * by hand, see `SHELEM` and `PASUR` there). بلوف still has both, and no master. «مار پله» (`ladders`,
 * 2026-11-03) is a board game cut like a card game: the operator's 2400×1792 master, `cut.cjs`'s
 * `LADDERS` box (the whole board, measured by hand) — banner, tile and راهنما square, no mirror.
 *
 * `Partial` is kept for safety (consumers already guard a missing entry), but every game —
 * chess + backgammon included — now has art, so the lobby tile always renders a cover.
 */
export const GAME_ART: Partial<Record<GameId, string>> = {
  hokm,
  shelem,
  pasur,
  hearts,
  bluff,
  chess,
  backgammon,
  mensch,
  ladders,
  dooz,
};

/**
 * «مار پله»'s lobby tile's WebP TWIN (card-86 round 2, batch P) — beside `GAME_ART`, never instead of
 * it: the JPEG stays every `<img src>` (the real fallback, `components/ArtPicture.tsx`). The twin ships
 * only when the art tools' MEASUREMENT passed one (`tools/card-art/webp.cjs`: at least as close to the
 * master as the JPEG, no visible difference from it, fewer bytes — the operator's «quality must not
 * drop at all»); on 2026-11-03 none did, so this map is EMPTY and every site draws the JPEG alone.
 * FOUND, not imported, so a twin the tools write later is picked up with no code change.
 */
const LADDERS_TILE_WEBP = import.meta.glob<string>("./games/ladders.webp", {
  eager: true,
  query: "?url",
  import: "default",
})["./games/ladders.webp"];
export const GAME_ART_WEBP: Partial<Record<GameId, string>> = LADDERS_TILE_WEBP
  ? { ladders: LADDERS_TILE_WEBP }
  : {};

/** …and its راهنما square's (`screens/Guides.tsx`; `tools/card-art/cut.cjs`'s guide) — the same rule. */
const LADDERS_GUIDE_WEBP = import.meta.glob<string>("./guides/ladders.webp", {
  eager: true,
  query: "?url",
  import: "default",
})["./guides/ladders.webp"];
export const GUIDE_ART_WEBP: Readonly<Partial<Record<string, string>>> = LADDERS_GUIDE_WEBP
  ? { ladders: LADDERS_GUIDE_WEBP }
  : {};

/**
 * «مار» (Snake)'s lobby picture — its own slot, since Snake is not a `GameId` (it is not in the
 * multi-player catalogue). The operator's 2400×1792 render (2026-11-02: an old candy-bar phone on the
 * felt, the snake on its LCD), archived as `docs/stitch/board-guide-art/snake-master.jpeg` and cut by
 * `apps/web/tools/card-art/cut.cjs` (`SNAKE`) exactly as the other tiles are — 420×313, q90. The TILE
 * only: Snake has no invite banner (so no `public/banners/` mirror), no راهنما row and no «برترین‌ها»
 * band.
 */
export const SNAKE_ART: string = snake;

/**
 * «سودوکو»'s lobby picture — its own slot too (Sudoku is not a `GameId` either). The operator's
 * 2400×1792 render (2026-11-03: a paper sudoku on the felt, a pencil and an eraser beside it), archived
 * as `docs/stitch/board-guide-art/sudoku-master.jpeg` and cut by `cut.cjs` (`SUDOKU`) like «مار»'s —
 * 420×313, q90, the tile only.
 */
export const SUDOKU_ART: string = sudoku;
